// [claude-code 2026-03-27] S2-T1: Commentator tier system — 3 tiers for weighting official statements

export const COMMENTATOR_TIERS = [1, 2, 3] as const;
export type CommentatorTier = (typeof COMMENTATOR_TIERS)[number];

export interface CommentatorEntry {
  id: string;
  name: string;
  aliases: string[]; // ["Jerome Powell", "Powell", "Fed Chair Powell"]
  tier: CommentatorTier;
  role?: string; // "Fed Chair", "Treasury Secretary"
  institution?: string; // "Federal Reserve", "US Treasury"
  weightMultiplier: number; // Tier 1 = 1.5, Tier 2 = 1.2, Tier 3 = 1.0
  rank: number; // Position in the ranked list (1 = most important). Drag-and-drop reorder.
  active: boolean;
  createdAt: string;
}

// [claude-code 2026-03-28] S9-T2: Updated default Persons of Interest per brief spec.
// Top 8 POIs from the brief are Tier 1/2. Remaining Fed governors etc. kept as Tier 2/3.
/** Default Persons of Interest roster — seeded on first load */
export const DEFAULT_COMMENTATORS: Omit<
  CommentatorEntry,
  "id" | "createdAt"
>[] = [
  // ── Top 8 Persons of Interest (per S9-T2 brief) ──
  {
    name: "Jerome Powell",
    aliases: ["Powell", "Fed Chair Powell", "Chair Powell"],
    tier: 1,
    role: "Fed Chair, data-dependent",
    institution: "Federal Reserve",
    weightMultiplier: 1.5,
    rank: 1,
    active: true,
  },
  {
    name: "Donald Trump",
    aliases: ["Trump", "POTUS", "President Trump"],
    tier: 1,
    role: "Executive, tariff hawk",
    institution: "White House",
    weightMultiplier: 1.5,
    rank: 2,
    active: true,
  },
  {
    name: "Scott Bessent",
    aliases: ["Bessent", "Treasury Secretary Bessent"],
    tier: 1,
    role: "Treasury Secretary",
    institution: "US Treasury",
    weightMultiplier: 1.5,
    rank: 3,
    active: true,
  },
  {
    name: "Marco Rubio",
    aliases: ["Rubio", "Secretary Rubio", "Senator Rubio"],
    tier: 2,
    role: "Secretary of State, foreign policy",
    institution: "State Department",
    weightMultiplier: 1.2,
    rank: 4,
    active: true,
  },
  {
    name: "Howard Lutnick",
    aliases: ["Lutnick", "Commerce Secretary Lutnick"],
    tier: 2,
    role: "Commerce Secretary",
    institution: "Dept. of Commerce",
    weightMultiplier: 1.2,
    rank: 5,
    active: true,
  },
  {
    name: "Steve Witkoff",
    aliases: ["Witkoff", "Envoy Witkoff"],
    tier: 2,
    role: "Middle East envoy",
    institution: "White House",
    weightMultiplier: 1.2,
    rank: 6,
    active: true,
  },
  {
    name: "Jamieson Greer",
    aliases: ["Greer", "USTR Greer"],
    tier: 2,
    role: "US Trade Representative",
    institution: "USTR",
    weightMultiplier: 1.2,
    rank: 7,
    active: true,
  },
  // ── Tier 2: Iran Geopolitical Adversary Officials — NOW rank 8-13 ──
  // Market-moving: oil spikes, gold, risk premia, Middle East war premium
  {
    name: "Ali Khamenei",
    aliases: ["Khamenei", "Supreme Leader", "Ayatollah"],
    tier: 2,
    role: "Supreme Leader of Iran — final authority on military escalation",
    institution: "Iran",
    weightMultiplier: 1.2,
    rank: 8,
    active: true,
  },
  {
    name: "Abbas Araghchi",
    aliases: ["Araghchi", "Iran FM", "Iran foreign minister", "Iranian FM"],
    tier: 2,
    role: "Iran Foreign Minister, nuclear/sanctions provocateur",
    institution: "Iran",
    weightMultiplier: 1.2,
    rank: 9,
    active: true,
  },
  {
    name: "Hossein Salami",
    aliases: ["Salami", "IRGC Commander", "IRGC chief"],
    tier: 2,
    role: 'IRGC Commander-in-Chief, "finger on the trigger"',
    institution: "IRGC",
    weightMultiplier: 1.2,
    rank: 10,
    active: true,
  },
  {
    name: "Masoud Pezeshkian",
    aliases: ["Pezeshkian", "Iran president", "Iranian president"],
    tier: 2,
    role: "President of Iran",
    institution: "Iran",
    weightMultiplier: 1.2,
    rank: 11,
    active: true,
  },
  {
    name: "Kamal Kharrazi",
    aliases: ["Kharrazi", "Iran strategic council"],
    tier: 2,
    role: 'Head of Iran Strategic Council, "ready for long war"',
    institution: "Iran",
    weightMultiplier: 1.2,
    rank: 12,
    active: true,
  },
  {
    name: "Ali Shamkhani",
    aliases: ["Shamkhani", "Iran security chief"],
    tier: 2,
    role: "Secretary of Supreme National Security Council",
    institution: "Iran",
    weightMultiplier: 1.2,
    rank: 13,
    active: true,
  },
  {
    name: "Peter Navarro",
    aliases: ["Navarro", "Adviser Navarro"],
    tier: 2,
    role: "Trade advisor, protectionist",
    institution: "White House",
    weightMultiplier: 1.2,
    rank: 14,
    active: true,
  },
  // ── Tier 1: Private Sector Market Movers ──
  {
    name: "Elon Musk",
    aliases: ["Musk", "DOGE Musk"],
    tier: 1,
    role: "DOGE / Tesla / xAI",
    institution: "Tesla / xAI / DOGE",
    weightMultiplier: 1.5,
    rank: 15,
    active: true,
  },
  {
    name: "Jamie Dimon",
    aliases: ["Dimon", "JPM Dimon"],
    tier: 1,
    role: "JPMorgan CEO, credit bellwether",
    institution: "JPMorgan Chase",
    weightMultiplier: 1.5,
    rank: 16,
    active: true,
  },
  {
    name: "Larry Fink",
    aliases: ["Fink", "BlackRock Fink"],
    tier: 1,
    role: "BlackRock CEO, $10T AUM",
    institution: "BlackRock",
    weightMultiplier: 1.5,
    rank: 17,
    active: true,
  },
  // ── Tier 2: Power Brokers & Tech Regime ──
  {
    name: "Peter Thiel",
    aliases: ["Thiel", "Founders Fund Thiel"],
    tier: 2,
    role: "Founders Fund / Palantir, political kingmaker",
    institution: "Founders Fund / Palantir",
    weightMultiplier: 1.2,
    rank: 18,
    active: true,
  },
  {
    name: "Jared Kushner",
    aliases: ["Kushner", "Envoy Kushner"],
    tier: 2,
    role: "ME back-channel, Affinity Partners",
    institution: "White House (informal) / Affinity Partners",
    weightMultiplier: 1.2,
    rank: 19,
    active: true,
  },
  {
    name: "Sam Altman",
    aliases: ["Altman", "OpenAI Altman"],
    tier: 2,
    role: "OpenAI CEO, Stargate lead",
    institution: "OpenAI",
    weightMultiplier: 1.2,
    rank: 20,
    active: true,
  },
  {
    name: "Dario Amodei",
    aliases: ["Amodei", "Anthropic Amodei"],
    tier: 2,
    role: "Anthropic CEO, AI safety/scale",
    institution: "Anthropic",
    weightMultiplier: 1.2,
    rank: 21,
    active: true,
  },
  {
    name: "Mark Zuckerberg",
    aliases: ["Zuckerberg", "Zuck", "Meta Zuckerberg"],
    tier: 2,
    role: "Meta CEO, AI capex pivot",
    institution: "Meta Platforms",
    weightMultiplier: 1.2,
    rank: 22,
    active: true,
  },
  {
    name: "Jensen Huang",
    aliases: ["Huang", "Jensen", "NVIDIA Huang"],
    tier: 2,
    role: "NVIDIA CEO, AI infrastructure kingpin",
    institution: "NVIDIA",
    weightMultiplier: 1.2,
    rank: 23,
    active: true,
  },
  {
    name: "Masayoshi Son",
    aliases: ["Son", "SoftBank Son", "Masa Son"],
    tier: 2,
    role: "SoftBank CEO, $550B Japan-US bridge",
    institution: "SoftBank Group",
    weightMultiplier: 1.2,
    rank: 24,
    active: true,
  },
  {
    name: "Kazuo Ueda",
    aliases: ["Ueda", "BOJ Ueda", "Governor Ueda"],
    tier: 2,
    role: "BoJ Governor, carry trade catalyst",
    institution: "Bank of Japan",
    weightMultiplier: 1.2,
    rank: 25,
    active: true,
  },
  {
    name: "Kevin Warsh",
    aliases: ["Warsh", "Fed Chair candidate Warsh"],
    tier: 2,
    role: "Fed Chair candidate, hawk",
    institution: "Federal Reserve (nominee)",
    weightMultiplier: 1.2,
    rank: 26,
    active: true,
  },
  {
    name: "Benjamin Netanyahu",
    aliases: ["Netanyahu", "Bibi", "Israeli PM"],
    tier: 2,
    role: "Israeli Prime Minister",
    institution: "Israel",
    weightMultiplier: 1.2,
    rank: 27,
    active: true,
  },
  {
    name: "Xi Jinping",
    aliases: ["Xi", "Xi Jinping", "Chinese president"],
    tier: 2,
    role: "Chinese President, trade war counterparty",
    institution: "China",
    weightMultiplier: 1.2,
    rank: 28,
    active: true,
  },
  // ── Extended roster (Fed officials, key analysts) ──
  {
    name: "Christopher Waller",
    aliases: ["Waller", "Fed Waller", "Fed's Waller"],
    tier: 2,
    role: "Fed Governor",
    institution: "Federal Reserve",
    weightMultiplier: 1.2,
    rank: 29,
    active: true,
  },
  {
    name: "Nick Timiraos",
    aliases: ["Timiraos", "WSJ Timiraos"],
    tier: 2,
    role: "Fed Whisperer",
    institution: "Wall Street Journal",
    weightMultiplier: 1.2,
    rank: 30,
    active: true,
  },
  {
    name: "Christine Lagarde",
    aliases: ["Lagarde", "ECB Lagarde"],
    tier: 2,
    role: "ECB President",
    institution: "European Central Bank",
    weightMultiplier: 1.2,
    rank: 31,
    active: true,
  },
  // ── Tier 3: Color Providers ──
  {
    name: "Austan Goolsbee",
    aliases: ["Goolsbee", "Fed Goolsbee", "Fed's Goolsbee"],
    tier: 3,
    role: "Chicago Fed President",
    institution: "Federal Reserve",
    weightMultiplier: 1.0,
    rank: 32,
    active: true,
  },
  {
    name: "Neel Kashkari",
    aliases: ["Kashkari", "Fed Kashkari", "Fed's Kashkari"],
    tier: 3,
    role: "Minneapolis Fed President",
    institution: "Federal Reserve",
    weightMultiplier: 1.0,
    rank: 33,
    active: true,
  },
  {
    name: "John Williams",
    aliases: ["Williams", "Fed Williams", "Fed's Williams"],
    tier: 3,
    role: "New York Fed President",
    institution: "Federal Reserve",
    weightMultiplier: 1.0,
    rank: 34,
    active: true,
  },
  {
    name: "Mary Daly",
    aliases: ["Daly", "Fed Daly", "Fed's Daly"],
    tier: 3,
    role: "San Francisco Fed President",
    institution: "Federal Reserve",
    weightMultiplier: 1.0,
    rank: 35,
    active: true,
  },
  {
    name: "Michelle Bowman",
    aliases: ["Bowman", "Fed Bowman", "Fed's Bowman"],
    tier: 3,
    role: "Fed Governor",
    institution: "Federal Reserve",
    weightMultiplier: 1.0,
    rank: 36,
    active: true,
  },
  {
    name: "Steve Schwarzman",
    aliases: ["Schwarzman", "Blackstone Schwarzman"],
    tier: 3,
    role: "Blackstone CEO, $1T alts",
    institution: "Blackstone",
    weightMultiplier: 1.0,
    rank: 37,
    active: true,
  },
  {
    name: "Ken Griffin",
    aliases: ["Griffin", "Citadel Griffin"],
    tier: 3,
    role: "Citadel CEO, market structure",
    institution: "Citadel",
    weightMultiplier: 1.0,
    rank: 38,
    active: true,
  },
  {
    name: "Stanley Druckenmiller",
    aliases: ["Druckenmiller", "Druck"],
    tier: 3,
    role: "Duquesne, macro positioning",
    institution: "Duquesne Family Office",
    weightMultiplier: 1.0,
    rank: 39,
    active: true,
  },
  {
    name: "Ray Dalio",
    aliases: ["Dalio", "Bridgewater Dalio"],
    tier: 3,
    role: "Bridgewater, regime framework",
    institution: "Bridgewater Associates",
    weightMultiplier: 1.0,
    rank: 40,
    active: true,
  },
  {
    name: "Sanae Takaichi",
    aliases: ["Takaichi", "PM Takaichi", "Japan PM"],
    tier: 3,
    role: "Japan PM, $550B counterparty",
    institution: "Government of Japan",
    weightMultiplier: 1.0,
    rank: 41,
    active: true,
  },
  {
    name: "Ryosei Akazawa",
    aliases: ["Akazawa", "Japan trade negotiator"],
    tier: 3,
    role: "Japan chief trade negotiator",
    institution: "Government of Japan",
    weightMultiplier: 1.0,
    rank: 42,
    active: true,
  },
  {
    name: "Atsushi Mimura",
    aliases: ["Mimura", "Japan FX official"],
    tier: 3,
    role: "Japan top currency official",
    institution: "Japan Ministry of Finance",
    weightMultiplier: 1.0,
    rank: 43,
    active: true,
  },
];

export const TIER_DEFAULT_MULTIPLIERS: Record<CommentatorTier, number> = {
  1: 1.5, // Market Movers — Fed Chair, Treasury Sec, POTUS
  2: 1.2, // Notable Officials — Governors, key Cabinet, Timiraos
  3: 1.0, // Color Providers — Regional Feds, analysts
};

export const UNTAGGED_MULTIPLIER = 0.8;
